import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { faPause, faPlay, faBan, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import BaseDialog from "../ui/BaseDialog";
import ActionButton from "../ui/ActionButton";

const ACTION_CONFIG = {
  pause: {
    title: "Pause Partnership",
    description: "Auto-requests will stop until resumed. Both sides can resume at any time.",
    confirmLabel: "Pause",
    confirmVariant: "warning",
    icon: { icon: faPause, variant: "warning" },
    placeholder: "Reason for pausing (e.g. warehouse full, seasonal shutdown)...",
  },
  resume: {
    title: "Resume Partnership",
    description: "Auto-requests will start firing again for new listings.",
    confirmLabel: "Resume",
    confirmVariant: "success",
    icon: { icon: faPlay, variant: "success" },
    placeholder: "Note (optional)...",
  },
  revoke: {
    title: "Revoke Partnership",
    description: "This permanently ends the partnership and blocks all users from both organizations from re-partnering. Only an admin can restore it.",
    confirmLabel: "Revoke",
    confirmVariant: "danger",
    icon: { icon: faBan, variant: "danger" },
    placeholder: "Reason for revoking (e.g. contract ended, compliance issue)...",
  },
  restore: {
    title: "Restore Partnership",
    description: "This reactivates the partnership and lifts the organization-level block, allowing auto-requests to resume.",
    confirmLabel: "Restore",
    confirmVariant: "success",
    icon: { icon: faRotateLeft, variant: "success" },
    placeholder: "Note (optional)...",
  },
};

const PartnershipActionModal = ({
  open,
  onClose,
  onConfirm,
  action,
  partnerName,
  darkMode,
  isProcessing = false,
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState("");
  const config = ACTION_CONFIG[action] || ACTION_CONFIG.pause;

  // reset notes when modal opens/closes
  useEffect(() => {
    if (open) setNotes("");
  }, [open]);

  const handleConfirm = () => {
    onConfirm(notes.trim() || null);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t(config.title)}
      darkMode={darkMode}
      icon={config.icon}
      ariaLabelledBy="partnership-action-modal-title"
      ariaDescribedBy="partnership-action-modal-description"
      actions={
        <>
          <ActionButton onClick={onClose} variant="default" modal disabled={isProcessing}>
            {t("Cancel")}
          </ActionButton>
          <ActionButton
            onClick={handleConfirm}
            variant={config.confirmVariant}
            modal
            loading={isProcessing}
          >
            {t(config.confirmLabel)}
          </ActionButton>
        </>
      }
    >
      {/* description */}
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: "0.95rem",
          color: darkMode ? "#e0e0e0" : "#666666",
          textAlign: "center",
          marginBottom: "0.5rem",
        }}
      >
        {partnerName && (
          <>
            {t("Partnership with")} <strong>{partnerName}</strong>
          </>
        )}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: "0.8rem",
          color: darkMode ? "#999" : "#888",
          textAlign: "center",
          marginBottom: "1rem",
        }}
      >
        {t(config.description)}
      </Typography>

      {/* notes textarea — reuses field-textarea from DetailedViewPanel */}
      <textarea
        className="field-textarea"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t(config.placeholder)}
        rows="3"
        maxLength={500}
        disabled={isProcessing}
      />
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: "0.65rem",
          color: darkMode ? "#666" : "#aaa",
          textAlign: "right",
          marginTop: "4px",
        }}
      >
        {notes.length}/500
      </Typography>
    </BaseDialog>
  );
};

PartnershipActionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  action: PropTypes.oneOf(["pause", "resume", "revoke", "restore"]).isRequired,
  partnerName: PropTypes.string,
  darkMode: PropTypes.bool.isRequired,
  isProcessing: PropTypes.bool,
};

export default PartnershipActionModal;
